import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Skeleton,
  Space,
  Typography,
  Upload,
} from "antd";
import Title from "antd/lib/typography/Title";
import axios from "axios";
import CreateOrEditContact from "components/EditCompanyContacts/CompanyContactBox/CreateOrEditContact";
import { message } from "components/Messages/Messages";
import { uploadButton } from "components/UploadButton/UploadButton";
import { JOB_TYPE_DESCRIPTIONS, yearLevels } from "constants/jobTypes";
import NeedHelpIcon from "containers/CreateCompanyProfile/NeedHelpIcon/NeedHelpIcon";
import { isCompanyUser, useLoginContext } from "contexts/LoginContext/LoginContext";
import dayjs from "dayjs";
import "dayjs/locale/nb";
import { getBase64 } from "helpers/functions";
import { CompanyContact, Sector } from "interfaces/companies";
import { Job } from "interfaces/jobs";
import moment from "moment";
import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import { Link, useHistory, useParams } from "react-router-dom";

const { Paragraph, Text } = Typography;
const { Option } = Select;

interface Props {
  existingJob?: boolean;
}

const CreateOrEditJobPosting: React.FC<Props> = ({ existingJob }) => {
  const [form] = Form.useForm();
  const { currentUser } = useLoginContext();
  const isCompany = isCompanyUser(currentUser);
  const [companyContacts, setCompanyContacts] = useState<CompanyContact[]>([]);
  const [showCreateContactModal, setShowCreateContactModal] = useState<boolean>(false);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const history = useHistory();
  const [job, setJob] = useState<Job>();
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [removeLogo, setRemoveLogo] = useState<boolean>(false);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    if (existingJob && currentUser) {
      axios.get<Job>(`/jobs/${currentUser?.id}/job-list/${id}/`).then(response => {
        setJob(response.data);
        setLogo(null);
        setLogoPreview(null);
        setRemoveLogo(false);
      });
    }
  }, [id, existingJob, currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    axios.get(`/companies/${currentUser?.id}/contact-persons/`).then(response => {
      setCompanyContacts(response.data);
    });
  }, [currentUser]);

  useEffect(() => {
    axios.get<Sector[]>("/companies/sectors/").then(response => {
      setSectors(response.data);
    });
  }, []);

  const createJobPosting = (values: Job) => {
    if (!isCompany) return;
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (!value || key === "deadline") return;
      if (value instanceof Array) {
        value.forEach(v => formData.append(key, String(v)));
        return;
      }
      formData.append(key, String(value));
    });
    formData.append("created_at", new Date().toISOString());
    formData.append("updated_at", new Date().toISOString());
    formData.append("company", currentUser.id.toString());
    formData.append("deadline", dayjs(values.deadline).endOf("day").toISOString());
    if (logo) formData.append("alt_logo", logo);
    if (attachment) formData.append("attachment", attachment);

    setLoading(true);
    axios
      .post<Job>(`/jobs/${currentUser.id}/job-list/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        history.push("/company/company-profile/");
        message("success", "Stillingsannonse opprettet");
        setLoading(false);
      })
      .catch(() => {
        message("error", "Kunne ikke opprette stillingsannonse");
        // TODO handle errors from backend when backend it set up
        setLoading(false);
      });
  };

  const updateJobPosting = (values: Job) => {
    if (!isCompany || !job) return;

    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (!value || key === "deadline") return;
      if (value instanceof Array) {
        value.forEach(v => formData.append(key, String(v)));
        return;
      }
      formData.append(key, String(value));
    });
    formData.append("updated_at", new Date().toISOString());
    formData.append("deadline", dayjs(values.deadline).endOf("day").toISOString());
    if (logo) formData.append("alt_logo", logo);
    if (removeLogo) formData.append("alt_logo", "");
    if (attachment) formData.append("attachment", attachment);

    setLoading(true);
    axios
      .patch<Job>(`/jobs/${currentUser.id}/job-list/${job.id}/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        history.push("/company/company-profile/");
        message("success", "Stillingsannonse oppdatert");
        setLoading(false);
      })
      .catch(() => {
        message("error", "Kunne ikke oppdatere stillingsannonse");
        // TODO handle errors from backend when backend it set up
        setLoading(false);
      });
  };

  const checkboxSectors = sectors.map(sector => {
    return (
      <Col key={sector.id} lg={4} md={6} sm={8} xs={12}>
        <Checkbox key={sector.id} value={sector.id}>
          {sector.name}
        </Checkbox>
      </Col>
    );
  });

  useEffect(() => {
    if (logo) {
      getBase64(logo)
        .then(url => setLogoPreview(url))
        .catch(() => setLogoPreview(null));
    }
    if (attachment) {
      getBase64(attachment)
        .then(url => setAttachmentPreview(url))
        .catch(() => setAttachmentPreview(null));
    }
  }, [logo, attachment]);

  if (existingJob && !job) return <Skeleton active />;

  return (
    <>
      <Title level={2}>{existingJob ? "Rediger stillingsannonse" : "Opprett ny stillingsannonse"}</Title>
      <Card>
        <Row style={{ justifyContent: "space-between", width: "100%" }}>
          <Col>
            <Title level={4}>Informasjon om stillingen</Title>
          </Col>
          <Col>
            <NeedHelpIcon />
          </Col>
        </Row>
        <Form
          form={form}
          layout="vertical"
          requiredMark
          scrollToFirstError
          onFinish={existingJob ? updateJobPosting : createJobPosting}
        >
          <Form.Item
            name="title"
            label="Tittel"
            initialValue={job?.title}
            rules={[{ required: true, message: "Vennligst skriv inn titel" }]}
          >
            <Input placeholder="Navn på stillingsannonsen" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Beskrivelse"
            initialValue={job?.description}
            rules={[{ required: true, message: "Vennligst skriv inn en beskrivelse" }]}
          >
            <ReactQuill />
          </Form.Item>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item label="Vedlegg">
                <Upload
                  listType="picture-card"
                  beforeUpload={file => {
                    setAttachment(file);
                    return false;
                  }}
                  fileList={
                    attachmentPreview
                      ? [
                          {
                            uid: "-1",
                            name: "Vedlegg",
                            status: "done",
                            url: attachmentPreview,
                          },
                        ]
                      : job?.attachment
                      ? [
                          {
                            uid: "-1",
                            name: job.attachment?.substring(job.attachment?.lastIndexOf("/") + 1) || "Vedlegg",
                            status: "done",
                            url: axios.defaults.baseURL + job.attachment,
                          },
                        ]
                      : []
                  }
                >
                  {uploadButton}
                </Upload>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Logo"
                help={
                  <Text style={{ color: "grey" }}>
                    Last opp en logo dersom du ønsker å benytte en annen logo enn bedriften sin egen.
                  </Text>
                }
                style={{ paddingBottom: "1.5em" }}
              >
                <Upload
                  listType="picture-card"
                  beforeUpload={file => {
                    setLogo(file);
                    setRemoveLogo(false);
                    return false;
                  }}
                  onRemove={() => {
                    setLogo(null);
                    setLogoPreview(null);
                    setRemoveLogo(true);
                  }}
                  fileList={
                    removeLogo
                      ? []
                      : logoPreview
                      ? [
                          {
                            uid: "-1",
                            name: "Alternativ logo",
                            status: "done",
                            url: logoPreview,
                          },
                        ]
                      : job?.alt_logo
                      ? [
                          {
                            uid: "-1",
                            name: job.alt_logo?.substring(job.alt_logo?.lastIndexOf("/") + 1) || "Logo",
                            status: "done",
                            url: job.alt_logo,
                          },
                        ]
                      : []
                  }
                >
                  {uploadButton}
                </Upload>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="year_levels"
            label="Årstrinn"
            initialValue={job?.year_levels}
            rules={[{ required: true, message: "Vennligst velg årstrinn stillingen er aktuell for" }]}
          >
            <Checkbox.Group options={yearLevels} />
          </Form.Item>
          <Form.Item
            name="job_type"
            label="Stillingstype"
            initialValue={job?.job_type}
            rules={[{ required: true, message: "Vennligst velg stillingstype" }]}
          >
            <Select options={JOB_TYPE_DESCRIPTIONS} placeholder="Velg stillingstype" />
          </Form.Item>
          <Form.Item
            name="location"
            label="Arbeidssted"
            initialValue={job?.location}
            rules={[{ required: true, message: "Vennligst skriv inn en arbiedssted" }]}
            help={<Text style={{ color: "grey" }}>Sepparer med komma</Text>}
            style={{ paddingBottom: "1.5em" }}
          >
            <Input placeholder="Arbeidsted for stillingen" />
          </Form.Item>
          <Form.Item
            name="sectors"
            label="Bransje"
            initialValue={job?.sectors}
            rules={[{ required: true, message: "Vennligst velg minst en bransje stillingen er aktuell for" }]}
          >
            <Checkbox.Group>
              <Row style={{ minHeight: "66px" }}>{checkboxSectors}</Row>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item name="company_contact" label="Kontaktperson" initialValue={job?.company_contact}>
            <Select
              placeholder="Velg kontaktperson for stillingen"
              dropdownRender={menu => (
                <>
                  {menu}
                  <Divider style={{ margin: "8px 0" }} />
                  <Space align="end" direction="vertical" style={{ padding: "0 8px 4px", float: "right" }}>
                    <CreateOrEditContact
                      companyId={currentUser?.id}
                      isOpen={showCreateContactModal}
                      onClose={() => setShowCreateContactModal(false)}
                      handleCreate={contact => setCompanyContacts(prevContacts => [...prevContacts, contact])}
                    />
                    <Button type="primary" onClick={() => setShowCreateContactModal(true)}>
                      Ny kontaktperson
                    </Button>
                  </Space>
                </>
              )}
            >
              {companyContacts?.map(contact => {
                return (
                  <Option key={contact.id} value={contact.id}>
                    {contact.first_name} {contact.last_name}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            name="deadline"
            label="Søknadsfrist"
            initialValue={job?.deadline ? moment(job?.deadline) : null}
            rules={[{ required: true, message: "Vennligst velg en søknadsfrist" }, { type: "object" }]}
          >
            <DatePicker
              format="DD.MM.YYYY"
              placeholder="Velg dato"
              disabledDate={current => {
                return moment().add(-1, "days") >= current;
              }}
              style={{ width: "100%" }}
            />
            {/* TODO change language to norwegian TODO time must be added and correct timezone */}
          </Form.Item>
          <Form.Item
            name="search_url"
            label="Link til søknadsskjema"
            initialValue={job?.search_url}
            rules={[{ type: "url", message: "Oppgi en gyldig url" }]}
          >
            <Input placeholder="Link til søknadsskjema (dersom det finnes)" />
          </Form.Item>
          <Paragraph style={{ fontSize: "0.8em", color: "grey", fontStyle: "italic", width: "100%" }}>
            Stillingen(e) som annonseres må kreve at søkere har påbegynt, eller fullført, høyere utdanning.
          </Paragraph>
          <Paragraph style={{ fontSize: "0.8em", color: "grey", fontStyle: "italic", width: "100%" }}>
            Stillingne(e) kan ikke ikke promotere eksterne arrangementer som kolliderer med arrangementer organisert av
            Bindeleddet NTNU.
          </Paragraph>
          <Paragraph style={{ fontSize: "0.8em", color: "grey", fontStyle: "italic", width: "100%" }}>
            Bindeleddet NTNU forbeholder seg retten til å fjerne annonser som ikke oppfyller disse kravene.
          </Paragraph>

          <Row style={{ justifyContent: "space-between", width: "100%", padding: "10px 0" }}>
            <Link to="/company/company-profile/jobs">
              <Button type="primary" danger size="large">
                Avbryt
              </Button>
            </Link>
            <Button
              type="primary"
              size="large"
              style={{ backgroundColor: "#59c09c", color: "var(--light)" }}
              htmlType="submit"
              loading={loading}
            >
              {existingJob ? "Oppdater annonse" : "Opprett anonnse"}
            </Button>
          </Row>
        </Form>
      </Card>
    </>
  );
};

export default CreateOrEditJobPosting;
